/**
 * Pure state logic for the Studio DOCX source viewer (WS-D0b) — the "보기" leg of the
 * source-verification axiom. Kept free of React so extraction, honest-stat shaping,
 * locator dereference, and large-document windowing are all unit-testable in isolation.
 *
 * The extractor itself lives in `@cogo/source-document`. It is imported here ONLY via
 * its browser-safe subpath (`@cogo/source-document/docx/extract`, pure fflate + @xmldom
 * TypeScript) and lazily (`await import(...)`), so neither the `node:crypto` bundle
 * builder nor the parser's ~parse weight lands in Studio's eager chunk — the parser +
 * its two deps become a separate chunk fetched only when a document is actually opened.
 */

import type {
  DocxBlock,
  DocxExtraction,
  DocxExtractionStats,
} from "@cogo/source-document/docx/extract";
import {
  resolveDocxLocator,
  type DocxLocatorResolution,
} from "@cogo/source-document/docx/locator";
import {
  loadDocumentSource,
  type DocumentViewerLoadResult,
} from "./document-source-model";

export type {
  DocumentSourceFormat,
  DocumentStatTile,
  DocumentViewerErrorModel,
  DocumentViewerLoadedModel,
  DocumentViewerLoadResult,
} from "./document-source-model";
import {
  clearAllDocumentSourcesForLogout,
  clearDocumentSource,
  clearDocumentSourceIfMatches,
  documentSourceScopeKey,
  readDocumentSource,
  saveDocumentSource,
  type DocumentSourceScope,
  type DocumentSourceRejectionReason,
} from "./document-source-storage";
import {
  persistDocumentSource,
  type PersistDocumentSourceResult,
} from "./document-source-platform-store";
import { useStudioStore } from "@/store/studio-store";

/** Default blocks-per-page for the paginated block list (large-doc windowing). */
export const DOCX_VIEWER_PAGE_SIZE = 200;

export interface DocxViewerLoadedModel {
  ok: true;
  fileName: string;
  byteSize: number;
  extraction: DocxExtraction;
}

export interface DocxViewerErrorModel {
  ok: false;
  fileName: string;
  byteSize: number;
  /** The extractor's typed `DocxExtractionErrorCode` (or `"unknown"`). */
  errorCode: string;
  message: string;
}

export type DocxViewerLoadResult = DocxViewerLoadedModel | DocxViewerErrorModel;

/**
 * Run the DOCX extractor over raw `bytes` IN THE BROWSER. Returns a discriminated
 * result rather than throwing: a corrupt / non-conformant source becomes a typed
 * error model (fail-closed, honest) that the UI renders as an explicit error banner —
 * never a silent partial.
 */
export async function loadDocxSource(
  bytes: Uint8Array,
  fileName: string,
): Promise<DocxViewerLoadResult> {
  const byteSize = bytes.byteLength;
  try {
    const { extractDocxSource } = await import(
      "@cogo/source-document/docx/extract"
    );
    const extraction = extractDocxSource(bytes);
    return { ok: true, fileName, byteSize, extraction };
  } catch (error) {
    const errorCode =
      error && typeof error === "object" && "code" in error
        ? String((error as { code: unknown }).code)
        : "unknown";
    const message =
      error instanceof Error ? error.message : String(error ?? "unknown error");
    return { ok: false, fileName, byteSize, errorCode, message };
  }
}

// --- Retained viewer session (survives unmount AND reload) --------------------
//
// The parsed document used to live in the component's `useState`, and the
// viewer is rendered inside `SourceSurface`, which unmounts on TWO ordinary
// interactions: switching the Design screen away from Canvas, and switching
// the left rail away from Source. Either one silently destroyed a parsed
// document with no warning and no way back — the operator had to find and
// re-upload the file. That is a state-ownership bug, not a storage problem:
// the bytes were already parsed, we simply threw the result away.
//
// So the session lives here, in module scope, and the component binds to it.
// The RELOAD boundary is closed separately, by `document-source-storage.ts`:
// the original bytes go to IndexedDB and are re-parsed on the next mount.
// Storage can refuse (quota, private mode), so `persistence` records what
// actually happened and `describeDocxRetention` reports only that — the notice
// never claims a durability the browser did not grant.

/** What the last write to browser storage actually achieved. */
export type DocumentPersistenceState =
  | { state: "unknown" }
  | { state: "stored"; savedAt: number }
  | { state: "rejected"; reason: DocumentSourceRejectionReason };

export interface DocxViewerSession {
  model: DocumentViewerLoadResult | null;
  locatorQuery: string;
  locatorResult: unknown;
  highlightIndex: number | null;
  pageIndex: number;
  persistence: DocumentPersistenceState;
  /** True while a stored document is being read back and re-parsed. */
  restoring: boolean;
}

const EMPTY_DOCX_VIEWER_SESSION: DocxViewerSession = {
  model: null,
  locatorQuery: "",
  locatorResult: null,
  highlightIndex: null,
  pageIndex: 0,
  persistence: { state: "unknown" },
  restoring: false,
};

const UNSCOPED_SESSION_KEY = "session:unscoped";
const docxViewerSessions = new Map<string, DocxViewerSession>();
const docxViewerSessionListeners = new Set<() => void>();
const documentPublicationQueues = new Map<string, Promise<void>>();
const documentPublicationGenerations = new Map<string, number>();
const documentPlatformAbortControllers = new Map<string, AbortController>();

function nextDocumentPublicationGeneration(sessionKey: string): number {
  documentPlatformAbortControllers.get(sessionKey)?.abort();
  const generation = (documentPublicationGenerations.get(sessionKey) ?? 0) + 1;
  documentPublicationGenerations.set(sessionKey, generation);
  return generation;
}

function documentPublicationUploadId(generation: number): string {
  const entropy = globalThis.crypto?.randomUUID?.().replaceAll("-", "")
    ?? `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
  return `studio-source-${generation}-${entropy}`;
}

function currentDocumentSourceScope(
  options?: DocumentSourceOpenOptions,
): DocumentSourceScope | null {
  const state = useStudioStore.getState();
  const principalId =
    options?.principalId?.trim() || state.session?.user?.id?.trim() || "";
  const projectId =
    options?.projectId?.trim() ||
    state.currentProjectId?.trim() ||
    "workspace_local";
  return principalId ? { principalId, projectId } : null;
}

function viewerSessionKey(scope: DocumentSourceScope | null): string {
  return scope
    ? documentSourceScopeKey(scope) ?? UNSCOPED_SESSION_KEY
    : UNSCOPED_SESSION_KEY;
}

function getSessionForKey(key: string): DocxViewerSession {
  return docxViewerSessions.get(key) ?? EMPTY_DOCX_VIEWER_SESSION;
}

function setSessionForKey(
  key: string,
  patch: Partial<DocxViewerSession>,
): void {
  docxViewerSessions.set(key, { ...getSessionForKey(key), ...patch });
  for (const listener of docxViewerSessionListeners) listener();
}

function replaceSessionForKey(key: string, session: DocxViewerSession): void {
  docxViewerSessions.set(key, session);
  for (const listener of docxViewerSessionListeners) listener();
}

async function runDocumentPublication<T>(
  sessionKey: string,
  publish: () => Promise<T>,
): Promise<T> {
  const previous = documentPublicationQueues.get(sessionKey) ?? Promise.resolve();
  const operation = previous.then(publish);
  const tail = operation.then(
    () => undefined,
    () => undefined,
  );
  documentPublicationQueues.set(sessionKey, tail);
  try {
    return await operation;
  } finally {
    if (documentPublicationQueues.get(sessionKey) === tail) {
      documentPublicationQueues.delete(sessionKey);
    }
  }
}

export function getDocxViewerSession(): DocxViewerSession {
  return getSessionForKey(viewerSessionKey(currentDocumentSourceScope()));
}

export function subscribeDocxViewerSession(listener: () => void): () => void {
  docxViewerSessionListeners.add(listener);
  return () => docxViewerSessionListeners.delete(listener);
}

/** Merge a patch into the retained session and notify subscribers. */
export function setDocxViewerSession(patch: Partial<DocxViewerSession>): void {
  setSessionForKey(viewerSessionKey(currentDocumentSourceScope()), patch);
}

export function resetDocxViewerSession(): void {
  for (const controller of documentPlatformAbortControllers.values()) {
    controller.abort();
  }
  docxViewerSessions.clear();
  documentPublicationQueues.clear();
  documentPublicationGenerations.clear();
  documentPlatformAbortControllers.clear();
  restoreAttempted.clear();
  for (const listener of docxViewerSessionListeners) listener();
}

// --- Open / restore orchestration --------------------------------------------

/**
 * One-shot guard for the mount-time restore. The viewer has TWO mount points
 * (the Source surface and the `/studio/import` document card) and both can be
 * alive at once, so without this a single reload would read and re-parse the
 * stored bytes twice.
 */
const restoreAttempted = new Set<string>();

/** Test seam — the module-scoped guard must not leak between cases. */
export function resetDocumentRestoreAttempt(): void {
  restoreAttempted.clear();
}

/**
 * Parse `bytes`, publish them to the session, then try to make them survive a
 * reload. The parse result is published BEFORE the storage attempt so a slow or
 * refusing store never delays the document appearing — and the persistence
 * badge updates from `unknown` to the truth a moment later.
 */
export type DocumentSourceOpenOptions = {
  principalId?: string | null;
  projectId?: string | null;
  contentType?: string;
  /** Prevent a superseded parse from publishing to the shared session/store. */
  signal?: AbortSignal;
  /** Selection-generation seam owned by the initiating surface. */
  isCurrentSelection?: () => boolean;
};

export type DocumentSourceOpenResult = DocumentViewerLoadResult & {
  persist?: PersistDocumentSourceResult;
};

export async function openDocumentSource(
  bytes: Uint8Array,
  fileName: string,
  options?: DocumentSourceOpenOptions,
): Promise<DocumentSourceOpenResult> {
  const scope = currentDocumentSourceScope(options);
  const sessionKey = viewerSessionKey(scope);
  const publicationGeneration = nextDocumentPublicationGeneration(sessionKey);
  const uploadId = documentPublicationUploadId(publicationGeneration);
  const model = await loadDocumentSource(bytes, fileName);
  const isCurrent = () =>
    documentPublicationGenerations.get(sessionKey) === publicationGeneration &&
    !options?.signal?.aborted &&
    (options?.isCurrentSelection?.() ?? true);

  return runDocumentPublication(sessionKey, async () => {
    // Parsing is intentionally async and can outlive the File selection that
    // started it. The generation is checked again after every async boundary.
    if (!isCurrent()) return model;
    const previousSession = getSessionForKey(sessionKey);
    setSessionForKey(sessionKey, {
      model,
      locatorQuery: "",
      locatorResult: null,
      highlightIndex: null,
      pageIndex: 0,
      persistence: { state: "unknown" },
      restoring: false,
    });
    // An unreadable source is not stored: restoring it would only reproduce
    // the same error banner on every future load.
    if (!model.ok) return model;

    if (!scope) {
      if (!isCurrent()) {
        if (getSessionForKey(sessionKey).model === model) {
          replaceSessionForKey(sessionKey, previousSession);
        }
        return model;
      }
      setSessionForKey(sessionKey, {
        persistence: { state: "rejected", reason: "unavailable" },
      });
      return model;
    }

    if (!isCurrent()) {
      if (getSessionForKey(sessionKey).model === model) {
        replaceSessionForKey(sessionKey, previousSession);
      }
      return model;
    }
    const save = await saveDocumentSource(scope, fileName, bytes);
    if (!isCurrent()) {
      if (getSessionForKey(sessionKey).model === model) {
        replaceSessionForKey(sessionKey, previousSession);
      }
      if (save.state === "stored") {
        // Publication is serialized per scope, so no newer open can write
        // between this identity check and compensation. Still compare the
        // stored hash/name so logout or another authority is never deleted.
        await clearDocumentSourceIfMatches(scope, {
          sha256: save.sha256,
          fileName,
        });
      }
      return model;
    }
    setSessionForKey(sessionKey, {
      persistence:
        save.state === "stored"
          ? { state: "stored", savedAt: save.savedAt }
          : { state: "rejected", reason: save.reason },
    });
    if (save.state !== "stored" || !isCurrent()) return model;

    const platformAbortController = new AbortController();
    const abortPlatformSave = () => platformAbortController.abort();
    if (options?.signal?.aborted) abortPlatformSave();
    else {
      options?.signal?.addEventListener("abort", abortPlatformSave, {
        once: true,
      });
    }
    documentPlatformAbortControllers.set(sessionKey, platformAbortController);
    let persist: PersistDocumentSourceResult;
    try {
      persist = await persistDocumentSource({
        bytes,
        fileName,
        projectId: scope.projectId,
        contentType: options?.contentType ?? "",
        clientSha256: save.sha256,
        uploadId,
        signal: platformAbortController.signal,
      });
    } finally {
      options?.signal?.removeEventListener("abort", abortPlatformSave);
      if (
        documentPlatformAbortControllers.get(sessionKey) ===
        platformAbortController
      ) {
        documentPlatformAbortControllers.delete(sessionKey);
      }
    }
    if (!isCurrent()) {
      if (getSessionForKey(sessionKey).model === model) {
        replaceSessionForKey(sessionKey, previousSession);
      }
      await clearDocumentSourceIfMatches(scope, {
        sha256: save.sha256,
        fileName,
      });
      return {
        ...model,
        persist:
          persist.state === "success"
            ? { state: "rejected", reason: "orphaned-after-cancel" }
            : persist.state === "rejected"
              ? persist
              : { state: "rejected", reason: "aborted" },
      };
    }
    return { ...model, persist };
  });
}

/**
 * Read back the stored document once per page load and re-parse it through the
 * same extractor. Returns the restored model, or null when there is nothing
 * stored / nothing readable — both of which leave the viewer in its ordinary
 * empty state rather than showing a failure the operator did not cause.
 */
export async function restoreDocumentSource(): Promise<DocumentViewerLoadResult | null> {
  const scope = currentDocumentSourceScope();
  const sessionKey = viewerSessionKey(scope);
  if (!scope || restoreAttempted.has(sessionKey)) return null;
  restoreAttempted.add(sessionKey);
  if (getSessionForKey(sessionKey).model) return null;
  const publicationGeneration = nextDocumentPublicationGeneration(sessionKey);
  const isCurrent = () =>
    documentPublicationGenerations.get(sessionKey) === publicationGeneration;

  return runDocumentPublication(sessionKey, async () => {
    if (!isCurrent()) return null;
    setSessionForKey(sessionKey, { restoring: true });
    try {
      const stored = await readDocumentSource(scope);
      if (!isCurrent()) return null;
      if (!stored) {
        setSessionForKey(sessionKey, { restoring: false });
        return null;
      }
      const model = await loadDocumentSource(
        new Uint8Array(stored.bytes),
        stored.fileName,
      );
      if (!isCurrent()) return null;
      setSessionForKey(sessionKey, {
        model,
        locatorQuery: "",
        locatorResult: null,
        highlightIndex: null,
        pageIndex: 0,
        // It came OUT of the store, so it is by definition in the store.
        persistence: model.ok
          ? { state: "stored", savedAt: stored.savedAt }
          : { state: "unknown" },
        restoring: false,
      });
      return model;
    } catch {
      if (isCurrent()) setSessionForKey(sessionKey, { restoring: false });
      return null;
    }
  });
}

/**
 * Close the document AND delete it from browser storage.
 *
 * Persistence without a way out is a trap: once a reload brings the document
 * back, "Replace" is the only escape and there is no way to simply be rid of
 * it. The session is cleared first so the interface reacts immediately; the
 * store delete follows and cannot fail loudly (a delete that does not land
 * leaves a record the next open overwrites anyway).
 */
export async function clearDocumentViewerSource(): Promise<void> {
  const scope = currentDocumentSourceScope();
  const sessionKey = viewerSessionKey(scope);
  nextDocumentPublicationGeneration(sessionKey);
  docxViewerSessions.delete(sessionKey);
  for (const listener of docxViewerSessionListeners) listener();
  // A reset re-arms the restore guard, which would let the very next mount
  // read the record back. Re-arm it AFTER the delete instead: this session is
  // deliberately empty.
  restoreAttempted.add(sessionKey);
  await runDocumentPublication(sessionKey, async () => {
    if (scope) await clearDocumentSource(scope);
  });
}

/** Clear every in-memory and browser-durable document at the logout boundary. */
export async function clearAllDocumentViewerSourcesForLogout(): Promise<void> {
  const sessionKeys = new Set([
    ...documentPublicationGenerations.keys(),
    ...documentPublicationQueues.keys(),
    ...docxViewerSessions.keys(),
  ]);
  for (const sessionKey of sessionKeys) {
    nextDocumentPublicationGeneration(sessionKey);
  }
  docxViewerSessions.clear();
  restoreAttempted.clear();
  for (const listener of docxViewerSessionListeners) listener();
  await Promise.all(
    [...sessionKeys].map((sessionKey) =>
      runDocumentPublication(sessionKey, async () => undefined),
    ),
  );
  await clearAllDocumentSourcesForLogout();
}

/**
 * What we are allowed to TELL the operator about how long this document
 * survives. Stated up front, while the document is open — not after it is
 * gone. `retainedAcrossViews` is true because of the module-scoped session
 * above. `survivesReload` is true ONLY when a write actually completed: a
 * quota rejection or a storage-less browsing mode leaves it false, so the
 * notice cannot promise durability the browser refused to provide.
 */
export function describeDocxRetention(
  model: DocumentViewerLoadResult | null,
  persistence: DocumentPersistenceState = { state: "unknown" },
): {
  loaded: boolean;
  retainedAcrossViews: boolean;
  survivesReload: boolean;
  storageRejected: boolean;
  rejectionReason: DocumentSourceRejectionReason | null;
} {
  return {
    loaded: Boolean(model),
    retainedAcrossViews: true,
    survivesReload: persistence.state === "stored",
    storageRejected: persistence.state === "rejected",
    rejectionReason:
      persistence.state === "rejected" ? persistence.reason : null,
  };
}

// --- Locator dereference across formats --------------------------------------

/**
 * Dereference a locator against the normalized block list.
 *
 * Delegates to the SHARED resolver — the viewer must not fork the tier order
 * the "대조" fidelity gate uses — with one format-specific guard: HWPX paragraph
 * ordinals restart at 0 in every `Contents/sectionN.xml`
 * (`hwpx/extract.ts`, `paragraphOrdinal` is section-local), so in a
 * multi-section HWPX a bare integer names several paragraphs and the shared
 * resolver's `findIndex` would silently return the first. That is exactly the
 * "confident but wrong" answer the resolver is fail-closed to avoid, so a bare
 * ordinal is refused with the anchor form to use instead.
 */
export function resolveDocumentLocator(
  model: { blocks: DocxBlock[]; format: string; sectionCount: number },
  rawQuery: string,
): DocxLocatorResolution {
  const query = rawQuery.trim();
  if (
    model.format === "hwpx" &&
    model.sectionCount > 1 &&
    /^\d+$/.test(query)
  ) {
    return {
      status: "gap",
      query,
      reason: `Paragraph numbers restart in each of this document's ${model.sectionCount} sections, so "${query}" names more than one paragraph. Use a full anchor such as sec0:para:${query}.`,
    };
  }
  return resolveDocxLocator({ blocks: model.blocks }, rawQuery);
}

// --- Honest stat telemetry (8 + sections = 9 tiles) --------------------------

export interface DocxStatTile {
  key: keyof DocxExtractionStats;
  label: string;
  value: number;
}

/**
 * The nine extraction stats in a fixed, honest order. Every field D0a measures is
 * surfaced (paragraphs / tables / characters / hidden / field / image / unknown /
 * altSkipped, plus sections) so the viewer cannot silently hide a non-zero
 * hidden-character or unknown-element count.
 */
const STAT_FIELDS: ReadonlyArray<{
  key: keyof DocxExtractionStats;
  label: string;
}> = [
  { key: "sections", label: "Sections" },
  { key: "paragraphs", label: "Paragraphs" },
  { key: "tables", label: "Tables" },
  { key: "characters", label: "Characters" },
  { key: "hiddenCharacters", label: "Hidden chars" },
  { key: "fieldInstructions", label: "Field codes" },
  { key: "imageRefs", label: "Image refs" },
  { key: "alternateContentBranchesSkipped", label: "Alt skipped" },
  { key: "unknownElements", label: "Unknown elements" },
];

export function describeDocxStats(stats: DocxExtractionStats): DocxStatTile[] {
  return STAT_FIELDS.map(({ key, label }) => ({ key, label, value: stats[key] }));
}

// --- Locator dereference (fail-closed) ----------------------------------------
// Hoisted to `@cogo/source-document/docx/locator` (WS-D-fid) so the "보기" viewer and
// the "대조" source-fidelity gate share ONE dereference implementation — no forked
// copy. Re-exported here so this module's existing consumers (viewer component + tests)
// keep importing `resolveDocxLocator` and its types from the same place.

// `resolveDocxLocator` is imported at the top of this module (it is also needed
// in local scope by `resolveDocumentLocator`) and re-exported here, so existing
// consumers keep importing it from this path and there is still exactly ONE
// implementation.
export { resolveDocxLocator };
export type {
  DocxLocatorMatchedBy,
  DocxLocatorCandidate,
  DocxLocatorResolution,
} from "@cogo/source-document/docx/locator";

// --- Large-document windowing (no virtualization dependency) ------------------

export function pageCountFor(blockCount: number, pageSize: number): number {
  if (pageSize <= 0) return 1;
  return Math.max(1, Math.ceil(blockCount / pageSize));
}

export function pageIndexForBlock(blockIndex: number, pageSize: number): number {
  if (pageSize <= 0) return 0;
  return Math.floor(Math.max(0, blockIndex) / pageSize);
}

export interface DocxPageWindow {
  start: number;
  end: number;
  pageIndex: number;
  pageCount: number;
}

/** Clamp `pageIndex` into `[0, pageCount)` and return its `[start, end)` block slice. */
export function pageWindow(
  blockCount: number,
  pageSize: number,
  pageIndex: number,
): DocxPageWindow {
  const pageCount = pageCountFor(blockCount, pageSize);
  // Non-positive pageSize → a single page spanning ALL blocks (same fallback semantics as
  // pageCountFor, which reports one page). Without this, `start`/`end` collapse to an empty
  // slice and the block list blanks out even though pageCount claims one page.
  if (pageSize <= 0) {
    return { start: 0, end: blockCount, pageIndex: 0, pageCount };
  }
  const clamped = Math.min(Math.max(0, pageIndex), pageCount - 1);
  const start = clamped * pageSize;
  const end = Math.min(start + pageSize, blockCount);
  return { start, end, pageIndex: clamped, pageCount };
}
